import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { Flex, Box, Grid } from '@chatterbug/aaron'
import { t } from 'src/lib/i18n-react'
import { usePageParams } from 'src/utils/params'
import { getLocale } from 'src/config/globals'

import NudgeCard from 'src/ui/NudgeCard/NudgeCard'
import NudgeCardContent from 'src/ui/NudgeCard/subcomponents/NudgeCardContent'
import routes from 'src/lib/rails-routes/rails-routes'
import Calendar from 'src/ui/Calendar/Calendar'
import ConfirmationModal from 'src/ui/Modals/ConfirmationModal/ConfirmationModal'
import AddUnitModal from 'src/ui/Modals/AddUnitModal/AddUnitModal'
import Flag from 'src/ui/MainMenu/subcomponents/Flag/Flag'
import CoachMark from 'src/ui/CoachMark/CoachMark'
import DateUtil from 'src/lib/date-util'
import PaymentForm from 'src/components/PaymentForm/PaymentForm'

import TutorFilter from './subcomponents/TutorFilter/TutorFilter'
import AppointmentList from './subcomponents/AppointmentsList/AppointmentsList'
import SchedulePageSkeleton from './SchedulePageSkeleton'
import ScheduleOverbooked from './subcomponents/ScheduleOverbooked/ScheduleOverbooked'
import TutorPanel from './subcomponents/TutorPanel/TutorPanel'
import CreditExpirationReminder from './subcomponents/CreditExpirationReminder/CreditExpirationReminder'

const EVENTS = {
  url: '/schedule/available_bookings',
}

const INITIAL_MODAL_PROPS = {
  isOpen: false,
}

const LOCALE = getLocale()

const getBookModalProps = ({
  l2Label,
  l2Code,
  event,
  collectPaymentDetails,
  canPayWithSepa,
  isOverextended,
  perClassPrice,
  onConfirm,
}) => {
  const tutorName = event.extendedProps.preferred?.[1]
  const start = moment(event.start).locale(LOCALE).utc()
  const end = moment(event.end).locale(LOCALE).utc()

  const formattedDate = `${DateUtil.format(
    start,
    'cb_ddddMMMD'
  )} ${DateUtil.format(start, 'cb_hmma')} - ${DateUtil.format(end, 'cb_hmma')}`

  return {
    title: t('New Appointment'),
    message: t(
      'Do you want to book this appointment for a %{language} Live Lesson?',
      {
        language: l2Label,
      }
    ),
    content: (
      <Flex flexDirection="column" alignItems="center" maxWidth={500}>
        <Flex alignItems="center">
          <Flag languageCode={l2Code} />
          <Box ml="2x" variant="text.h4">
            {formattedDate}
          </Box>
        </Flex>
        {tutorName && (
          <>
            <Box mt="3x" variant="text.paragraph">
              {t('We will offer this appointment first to')}
            </Box>
            <Box my="1x" variant="text.h4">
              {tutorName}
            </Box>
            <Box
              variant="text.paragraph"
              color="gray60"
              fontSize="xs"
              textAlign="center"
              maxWidth={300}
            >
              {t(
                'However, if they are not available, we will automatically try to find a backup tutor.'
              )}
            </Box>
          </>
        )}
        {/* PAYMENT PART */}
        {collectPaymentDetails && (
          <Box my="2x">
            <Flex
              as="p"
              mb="2x"
              bg="blue90"
              p="2x"
              borderRadius="16px"
              maxWidth={500}
              flexWrap="true"
            >
              {t(
                "Your first live lesson is free, but we require a credit card to book it. We will only charge your card 10€ if you don't show up for the class."
              )}
            </Flex>
            {/* props taken from l-schedule-upcoming l.22 as this is what sends props to the same modal in the old UI */}
            <PaymentForm
              paymentSource="additional"
              paymentMethods={canPayWithSepa ? ['card', 'iban'] : ['card']}
              stripePublishedKey={window.stripe_public_key}
              buttonText={t('Book Appointment')}
              onStripeResponse={(paymentId) => {
                // from the old UI
                window.eventTracking.track('live lesson', 'reservation')
                // here the LL will be booked (after Stripe sent back a response), as if we pressed on the "native" modal confirm button
                onConfirm(paymentId)
              }}
            />
          </Box>
        )}
        {isOverextended && perClassPrice && !collectPaymentDetails && (
          <Flex
            as="p"
            mt="2x"
            p="2x"
            bg="red97"
            borderRadius={8}
            color="red44"
            borderColor="red44"
            borderWidth={1}
            borderStyle="solid"
          >
            {t(
              "You are low on pre-paid credits for this period and it's possible this lesson will put you over your limit. If so, you will be charged an extra %{price} for this lesson.",
              { price: perClassPrice }
            )}
          </Flex>
        )}
      </Flex>
    ),
    confirmButtonLabel: t('Book Appointment'),
    confirmButtonDisabledWithLabel: t('Booking...'),
  }
}

const transformedTutorData = (tutors, filters) => {
  const createFilterParams = (params = []) =>
    tutors.map((tutor) => ({
      name: tutor.name,
      avatarUrl: tutor.avatarUrl,
      login: tutor.login,
      isFavorite: tutor.tutor.isFavorite,
      isFullyBooked: tutor.tutor.bookableSlots.length < 1,
      checked: params.includes(tutor.id),
      id: tutor.id,
    }))

  try {
    const tutorsIdsFromParams = filters?.map((filter) => filter.split(':')[0])
    return createFilterParams(tutorsIdsFromParams)
  } catch {
    return createFilterParams()
  }
}

const SchedulePage = ({
  onUpdateAppointment,
  onUpdatePotentialAppointment,
  onBookAsTrainer,
  onBook,
  upcomingAppointments,
  bookableAppointments,
  potentialAppointments,
  handleCancelAppointment,
  tutors,
  canBookLesson,
  isOverbooked,
  isOverextended,
  onShowSnackbar,
  compactTimezone,
  allTutors,
  onChangeTutorRating,
  collectPaymentDetails,
  canPayWithSepa,
  availableCredits,
  monthlyCredits,
  monthlyCreditsEndAt,
  bundleCredits,
  bundleCreditsEndAt,
}) => {
  const { l2Label, l2Code, filters, icalToken, perClassPrice } = usePageParams()
  const [modalProps, setModalProps] = useState(INITIAL_MODAL_PROPS)
  const [tutorFilterParams, setTutorFilterParams] = useState(
    (filters && filters.split(',')) || []
  )
  const [showAddUnitModal, setShowAddUnitModal] = useState(false)

  const calendarRef = useRef()

  const tutorFilterProps =
    tutors && transformedTutorData(tutors, tutorFilterParams)

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi?.()
    calendarApi?.removeAllEventSources?.()
    if (tutorFilterParams && tutorFilterParams.length > 0) {
      calendarApi?.addEventSource?.({
        url: EVENTS.url,
        extraParams: { tutors: tutorFilterParams.join(','), preferred: 'all' },
      })
    } else {
      calendarApi?.addEventSource?.({
        url: EVENTS.url,
      })
    }
  }, [tutorFilterParams])

  const handleCalendarRefetch = () => {
    const calendarApi = calendarRef.current?.getApi?.()
    calendarApi?.refetchEvents?.()
  }

  const handleEventClick = ({ event }) => {
    if (event.extendedProps.event_type === 'appointment-live') {
      return // appointment is already booked
    }

    if (isOverbooked) {
      onShowSnackbar({
        message: t('You need a subscription to book more appointments'),
        type: 'error',
      })
    } else if (canBookLesson) {
      // newPaymentSource is an optional parameter which is needed in case it was a trial lesson behind paywall
      const onConfirm = async (newPaymentSource) => {
        await onBook(event, handleCalendarRefetch, newPaymentSource)
        setModalProps(INITIAL_MODAL_PROPS)
      }
      setModalProps({
        ...getBookModalProps({
          l2Label,
          l2Code,
          event,
          collectPaymentDetails,
          canPayWithSepa,
          isOverextended,
          perClassPrice,
          onConfirm,
        }),
        isOpen: true,
        showButtons: !collectPaymentDetails,
        onConfirm: onConfirm,
        onHide: () => setModalProps(INITIAL_MODAL_PROPS),
      })
    } else {
      setShowAddUnitModal(true)
    }
  }

  const handleCancel = (reasons, appointment) => {
    handleCancelAppointment(reasons, appointment, handleCalendarRefetch)
  }

  const handleTutorFilterChange = (tutors) => {
    const selectedTutors = tutors
      .filter((tutor) => tutor.checked === true)
      .map((t) => `${t.id}:${t.login}`)

    const queryParams =
      selectedTutors.length > 0 ? `?tutors=${selectedTutors}` : ''
    const newPath = `${window.location.pathname}${queryParams}`

    window.history.pushState('', '', newPath)
    setTutorFilterParams(selectedTutors)
  }

  const confirmedAppointments = upcomingAppointments.filter(
    (a) => a.status === 'confirmed'
  ).length
  const pendingAppointments = upcomingAppointments.filter(
    (a) => a.status === 'pending'
  ).length

  const appointmentCount = `${t(
    '%{smart_count} confirmed |||| %{smart_count} confirmed',
    { smart_count: confirmedAppointments }
  )}
  | ${t('%{smart_count} pending |||| %{smart_count} pending', {
    smart_count: pendingAppointments,
  })}
  `

  return (
    <SchedulePageSkeleton
      titleSection={t('Live Lessons')}
      creditReminderSection={
        availableCredits > 0 && // availableCredits will be 0 if the personal subscription is on pause
        (monthlyCredits > 0 || bundleCredits > 0) &&
        (monthlyCreditsEndAt || bundleCreditsEndAt) &&  (
          <CreditExpirationReminder
            monthlyCredits={monthlyCredits}
            monthlyCreditsEndAt={monthlyCreditsEndAt}
            bundleCredits={bundleCredits}
            bundleCreditsEndAt={bundleCreditsEndAt}
          />
        )
      }
      nudgeCardSection={
        <NudgeCard name="nudge_card:subscribe">
          <NudgeCardContent
            type="subscribe"
            nudgeUrl={routes.payments_subscription()}
          />
        </NudgeCard>
      }
      subtitleSection={
        <Flex
          width="100%"
          flexDirection={{ _: 'column', tablet: 'row' }}
          justifyContent={{ _: 'auto', tablet: 'space-between' }}
        >
          <Box as="h2" variant="text.h4" mb={{ _: '1x', tablet: 0 }}>
            {t('Upcoming Lessons')}
          </Box>
          <Box variant="text.subtitle">{appointmentCount}</Box>
        </Flex>
      }
      upcomingLessonsSection={
        icalToken && (
          <AppointmentList
            iCalUrl={routes.schedule_ical({ token: icalToken })}
            appointments={upcomingAppointments}
            bookableAppointments={bookableAppointments}
            potentialAppointments={isOverbooked ? null : potentialAppointments}
            onCancel={handleCancel}
            onBookAsTrainer={onBookAsTrainer}
            onUpdateAppointment={onUpdateAppointment}
            onUpdatePotentialAppointment={onUpdatePotentialAppointment}
          />
        )
      }
      tutorListSection={
        allTutors?.length > 0 && (
          <TutorPanel
            tutors={allTutors}
            onChangeTutorRating={onChangeTutorRating}
          />
        )
      }
      calendarSection={
        <Grid as="section" gridGap="1x">
          <Box as="h2" variant="text.h4">
            {t('Schedule a lesson')}
          </Box>
          <Grid gridTemplateColumns="auto 1fr" gridGap="1x" alignItems="center">
            {tutors?.length > 0 && !isOverbooked && (
              <>
                <Box>{t('Filter by')}</Box>
                <TutorFilter
                  filterProps={tutorFilterProps}
                  applyFilters={handleTutorFilterChange}
                />
              </>
            )}
          </Grid>
          <Flex alignItems="center">
            <Box as="p" variant="text.h5" mr="2x">
              {t('Available Slots')}
            </Box>
            <Box as="p" variant="text.subtitle" lineHeight="s">
              {t('All times in %{timezone} timezone', {
                timezone: compactTimezone,
              })}
            </Box>
          </Flex>
          <>
            {isOverbooked && <ScheduleOverbooked />}
            <CoachMark
              name="coach_mark:schedule_live_lesson"
              title={t('Find an Appointment')}
              description={t(
                'Pick the time slot that works best for your schedule.'
              )}
              align="above"
            >
              <Calendar
                filtersApplied={tutorFilterParams.length > 0}
                calendarRef={calendarRef}
                events={EVENTS}
                onEventClick={handleEventClick}
              />
            </CoachMark>
            {modalProps.isOpen && canBookLesson ? (
              <ConfirmationModal {...modalProps} />
            ) : (
              <AddUnitModal
                l2Code={l2Code}
                isOpen={showAddUnitModal}
                onHide={() => setShowAddUnitModal(false)}
                onClick={() => {
                  window.location = routes.advanced_my_course(l2Code)
                }}
              />
            )}
          </>
        </Grid>
      }
    />
  )
}

SchedulePage.propTypes = {
  onBook: PropTypes.func.isRequired,
  onUpdateAppointment: PropTypes.func,
  onUpdatePotentialAppointment: PropTypes.func,
  onBookAsTrainer: PropTypes.func.isRequired,
  upcomingAppointments: PropTypes.array,
  bookableAppointments: PropTypes.array,
  potentialAppointments: PropTypes.array,
  handleCancelAppointment: PropTypes.func.isRequired,
  tutors: PropTypes.array,
  canBookLesson: PropTypes.bool.isRequired,
  collectPaymentDetails: PropTypes.bool.isRequired,
  isOverbooked: PropTypes.bool.isRequired,
  isOverextended: PropTypes.bool.isRequired,
  onShowSnackbar: PropTypes.func,
  compactTimezone: PropTypes.string.isRequired,
  allTutors: PropTypes.array,
  onChangeTutorRating: PropTypes.func,
  canPayWithSepa: PropTypes.bool.isRequired,
  availableCredits: PropTypes.number,
  monthlyCredits: PropTypes.number,
  monthlyCreditsEndAt: PropTypes.string,
  bundleCredits: PropTypes.number,
  bundleCreditsEndAt: PropTypes.string,
}

export default SchedulePage
